/**
 * useQcpInvestigations — the ONE 30s poll behind the Quality door: the case
 * files (investigations), Merlin's routed quality threads, and the header
 * badge aggregates. The Quality Triage shell owns it and hands the state to
 * every tab through Outlet context (qualityTriageContext); the Ask tab runs
 * its own copy when it is mounted standalone. Pages never start a poll.
 *
 * Goes dark when the deploy has the feature flagged off (the list endpoint
 * 404s) so the page can hide the surface entirely.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  listInvestigations,
  listThreads,
  type DoorInvestigationRead,
  type DoorThreadRead,
} from '../../services/qcpInvestigations';
import {
  activeInvestigationCount,
  anyUnseenResponses,
  sortThreads,
} from './investigationsView';

const POLL_INTERVAL_MS = 30000; // matches the portal notification polling cadence

export interface QcpInvestigationsState {
  investigations: DoorInvestigationRead[];
  /** Merlin's routed quality threads (worst severity first). */
  threads: DoorThreadRead[];
  /** Not-completed/cancelled investigations (drives the header count). */
  activeCount: number;
  /** True when any investigation has an unviewed response (red dot). */
  anyUnseen: boolean;
  /**
   * False when the deploy has the feature flagged off (the endpoint 404s) —
   * the page hides the button/panel/menu entirely so the surface stays dark.
   */
  available: boolean;
  /**
   * True once the first threads fetch has settled (either way). The threads
   * list is the big one (~2 MB on the prod clone) and lands seconds after the
   * investigations, so a page that would say "not found" waits for this
   * rather than judging an empty list (kanban 445962e2). Absent in older
   * harness states: treated as loaded.
   */
  threadsLoaded?: boolean;
  refresh: () => void;
  /** Fold a mutated/created investigation (POST response) into the list. */
  applyUpdate: (updated: DoorInvestigationRead) => void;
  /** Fold a mutated thread back in (every status is kept — see below). */
  applyThreadUpdate: (updated: DoorThreadRead) => void;
}

/**
 * Owns the door's investigations list while the page is mounted: initial
 * fetch + 30s polling (paused when `enabled` is false, e.g. before auth).
 */
export function useQcpInvestigations(enabled: boolean): QcpInvestigationsState {
  const [investigations, setInvestigations] = useState<DoorInvestigationRead[]>(
    [],
  );
  const [threads, setThreads] = useState<DoorThreadRead[]>([]);
  const [available, setAvailable] = useState(true);
  const [threadsLoaded, setThreadsLoaded] = useState(false);

  const refresh = useCallback(() => {
    listInvestigations()
      .then((data) => {
        setAvailable(true);
        setInvestigations(data);
      })
      .catch((error: unknown) => {
        // Feature flagged off on this deploy → endpoint 404s → go dark.
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) setAvailable(false);
        // otherwise: polling is best-effort; keep what we have
      });
    // Routed threads ride the same poll; a 404 (older deploy without the
    // threads endpoint) just leaves the section empty — the case-file
    // surface itself stays up.
    listThreads('all')
      .then((data) => setThreads(sortThreads(data)))
      .catch(() => {
        // best-effort; keep what we have
      })
      .finally(() => setThreadsLoaded(true));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, refresh]);

  const applyUpdate = useCallback((updated: DoorInvestigationRead) => {
    setInvestigations((prev) =>
      prev.some((i) => i.id === updated.id)
        ? prev.map((i) => (i.id === updated.id ? updated : i))
        : [updated, ...prev],
    );
  }, []);

  const applyThreadUpdate = useCallback((updated: DoorThreadRead) => {
    // Keep EVERY status: the door's tabs show cases, done and dismissed
    // threads too, so an action moves a thread between tabs instead of
    // vanishing it.
    setThreads((prev) =>
      sortThreads(prev.map((t) => (t.id === updated.id ? updated : t))),
    );
  }, []);

  const activeCount = useMemo(
    () => activeInvestigationCount(investigations),
    [investigations],
  );
  const anyUnseen = useMemo(
    () => anyUnseenResponses(investigations),
    [investigations],
  );

  return {
    investigations,
    threads,
    activeCount,
    anyUnseen,
    available,
    threadsLoaded,
    refresh,
    applyUpdate,
    applyThreadUpdate,
  };
}
